import React from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useSnackbar } from '@goodfynd/react-web.lib.context';
import { ReviewStep, Step, StepWizard } from '@goodfynd/react-web.lib.forms';
import { dateUtil } from '@goodfynd/react-web.utils.date-util';
import {usePermissions} from "@hooks/usePermissions";
import { useRouter } from 'next/router';

import routes from '../../../config/routes';
import wizard from '../../../config/wizard';
import { useApp } from '../../../context/AppContext';
import { useEventDispatch } from '../../../context/EventContext';
import { useEventApi } from '../../../hooks/api';
import { useYupValidationResolver } from '../../../hooks/useYupValidationResolver';
import type { AddUpdateEventRequest } from '../../../types/events';
import { getDashboardRoutePath } from '../../../utils/routes-util';

import BasicInfo from './Steps/BasicInfo';
import EventDetails from './Steps/EventDetails';
import EventLocation from './Steps/EventLocation';
import EventTimes from './Steps/EventTimes';
import GoodfyndAdmin from './Steps/GoodfyndAdmin';
import OrganizerInfo from './Steps/OrganizerInfo';
import ReviewEvent from './Steps/ReviewEvent';
import { EventType } from './helpers';
import type {
  BasicInfoFormFields,
  EventData,
  EventDetailsFormFields,
  EventLocationFormFields,
  EventTimesFormFields,
  EventWizardProps,
  GoodfyndAdminFormFields,
  OrganizerInfoFormFields,
} from './types';
import {
  adminValidation,
  basicInfoValidation,
  eventDetailsValidation,
  eventLocationValidation,
  eventTimesValidation,
  organizationInfoValidation,
} from './validation';
export const EventWizard = ({ event }: EventWizardProps) => {
  const { user } = useApp();
  const { isAdmin} = usePermissions();
  const router = useRouter();
  const { refetchEvent } = useEventDispatch();
  const eventApi = useEventApi();
  const { openSnackbar } = useSnackbar();

  const handleSubmit = useMutation(
    async (data: EventData) => {
      const {
        image,
        organizationName,
        title = '',
        email = '',
        dates = [],
        eventName = '',
        firstName = '',
        lastName,
        phone = '',
        location,
        numberOfVendors = 0,
        adminEmails,
        end = new Date(),
        vendingFee = 0,
        posFee,
        orderingFee,
        dontRequireSubscription = true,
        phoneMobile,
        numberOfGuest = 0,
        description,
        rsvpSite,
        parkingInstructions,
        start = new Date(),
        setup = new Date(),
        notify = false,
        receiptImage,
        sponsorImage,
        rainOrShine = true,
        type,
        isPublic,
      } = data;

      if (!start || !end || !setup) {
        return Promise.reject();
      }
      const request: AddUpdateEventRequest = {
        title,
        organization: organizationName,
        address: location?.address,
        admins: adminEmails?.join(','),
        city: location?.city,
        country: location?.country,
        county: location?.county,
        firstName,
        lastName,
        name: eventName,
        dates: dates
          .map((date) => dateUtil.setTime(date, start).toISOString())
          .join(','),
        notify,
        sector: 'trucks',
        email,
        startTime: start,
        endTime: end,
        guestimates: numberOfGuest,
        description,
        phone,
        phoneMobile,
        coordinates: location?.coordinates ?? '',
        totalVendors: numberOfVendors,
        typeId: type,
        fee: vendingFee,
        subscriptionFree: dontRequireSubscription,
        posServiceFee: posFee,
        orderingServiceFee: orderingFee,
        rsvp: rsvpSite,
        parkingInstructions,
        setupTime: setup,
        receiptImage,
        sponsorImage,
        isFixed: rainOrShine,
        file: !image?.startsWith('http') ? image : undefined,
        isPublic,
      };
      return event
        ? await eventApi.updateEvent(event.id, request)
        : await eventApi.addEvent(request);
    },
    {
      onError: () => {
        openSnackbar({ type: 'error', message: 'Failed to save event' });
      },
      onSuccess: ({ entry }) => {
        openSnackbar({
          type: 'success',
          message: 'Event saved successfully',
        });

        refetchEvent();
        router.push(
          getDashboardRoutePath(routes.dashboard.info.home, entry.nameId)
        );
      },
    }
  );

  const basicInfoForm = useForm<BasicInfoFormFields>({
    defaultValues: {
      eventName: event?.name,
      organizationName: event?.organization,
      description: event?.description,
      image: event?.image,
      rsvpSite: event?.rsvp,
    },
    resolver: useYupValidationResolver(basicInfoValidation),
  });
  const organizerInfoForm = useForm<OrganizerInfoFormFields>({
    defaultValues: {
      adminEmails: event?.admins ? event?.admins.split(',') : undefined,
      title: event?.title,
      firstName: event?.firstName,
      lastName: event?.lastName,
      phone: event?.phoneNumber,
      email: isAdmin ? event?.email : event?.email || user.email,
      phoneMobile: event?.phoneMobile,
    },
    resolver: useYupValidationResolver(organizationInfoValidation),
  });
  const eventDetailsForm = useForm<EventDetailsFormFields>({
    defaultValues: {
      numberOfVendors: event?.totalVendors,
      vendingFee: event?.fee,
      numberOfGuest: event?.guestimates,
      rainOrShine: event?.isFixed,
      type: event ? Number(event.typeId) : EventType.Inside,
    },
    resolver: useYupValidationResolver(eventDetailsValidation),
  });
  const eventLocationForm = useForm<EventLocationFormFields>({
    defaultValues: {
      location: event?.map.baseLocation,
      parkingInstructions: event?.parkingInstructions,
    },
    resolver: useYupValidationResolver(eventLocationValidation),
  });
  const eventTimesForm = useForm<EventTimesFormFields>({
    defaultValues: {
      dates: event?.dates
        ?.map((date) => new Date(date))
        .filter((date) => dateUtil.isFuture(date)),
      start: event?.startTime
        ? dateUtil.setTime(new Date(), event?.startTime)
        : null,
      end: event?.endTime ? dateUtil.setTime(new Date(), event?.endTime) : null,
      setup: event?.setupTime
        ? dateUtil.setTime(new Date(), event?.setupTime)
        : null,
    },
    resolver: useYupValidationResolver(eventTimesValidation),
  });
  const adminForm = useForm<GoodfyndAdminFormFields>({
    defaultValues: {
      notify: false,
      orderingFee: event?.orderingServiceFee,
      posFee: event?.posServiceFee,
      receiptImage: event?.receiptImage,
      sponsorImage: event?.sponsorImage,
      dontRequireSubscription: event?.subscriptionFree,
      updatedBy: event?.updatedBy,
      uploadedBy: event?.uploadedBy,
      isPublic: event?.isPublic,
    },
    resolver: useYupValidationResolver(adminValidation),
  });

  return (
    <div>
      <StepWizard
        initialStep={String(router.query['step'])}
        isEditing={!!event?.id}
        isSubmitting={handleSubmit.isLoading}
        onSubmit={handleSubmit.mutate}
      >
        <Step
          form={basicInfoForm}
          renderForm={BasicInfo}
          stepKey={wizard.lot.basicInfo}
          title="Basic Info"
        />

        <Step
          form={organizerInfoForm}
          renderForm={OrganizerInfo}
          stepKey={wizard.lot.pointOfContacts}
          title="Event Organizer Information"
        />

        <Step
          form={eventDetailsForm}
          renderForm={EventDetails}
          stepKey={wizard.lot.details}
          title="Event Details"
        />

        <Step
          form={eventLocationForm}
          renderForm={EventLocation}
          stepKey={wizard.lot.location}
          title="Event Location"
        />

        <Step
          form={eventTimesForm}
          renderForm={EventTimes}
          stepKey={wizard.lot.times}
          title="Event Times"
        />

        {isAdmin && (
          <Step
            form={adminForm}
            renderForm={GoodfyndAdmin}
            stepKey={wizard.lot.admin}
            title="Goodfynd Admins Only"
          />
        )}
        <ReviewStep renderStep={ReviewEvent} />
      </StepWizard>
    </div>
  );
};
